import React from 'react';
import {Chip, Stack, TextField} from '@mui/material';
import {TFunction} from 'i18next';
import {
    getProposals,
    LINKED_READ_ONLY_INPUT_TYPE,
    PROPOSALS_INPUT_TYPE,
} from '../../utils/contactPersonFormAttributes';

// mirrors TDAForm's label rule: name plus " *" when a required rule exists
const getAttributeLabel = (attribute: any): string =>
    attribute?.rules?.some((rule: any) => rule?.name === 'required')
        ? `${attribute.name} *`
        : attribute?.name ?? '';

/** Where a linked contact's name/email/phone come from, worded per sourceKind. */
export const getLinkedSourceHint = (t: TFunction, sourceKind?: string | null): string =>
    sourceKind === 'SERVICE_PROVIDER_CONTACT'
        ? t('Provided by your service provider.')
        : t("Kept up to date from this person's OTTO account.");

/**
 * Custom TDAForm inputs for contact person forms (see contactPersonFormAttributes):
 * the free-text role with the backend's proposal chips below it, and the
 * read-only fields of a linked contact.
 */
export const contactPersonInputRenderers = () => ({
    [PROPOSALS_INPUT_TYPE]: ({attribute, value, error, onChange, onBlur, setFieldValue}: any) => {
        const proposals = getProposals(attribute);
        return (
            <div>
                {/* Mirrors TDAForm's stock text branch (TextFieldWithUnit without a unit) */}
                <TextField
                    id={attribute.index}
                    name={attribute.index}
                    fullWidth
                    size="small"
                    label={getAttributeLabel(attribute)}
                    value={value ?? ''}
                    onChange={onChange}
                    onBlur={onBlur}
                    disabled={!!attribute.disabled}
                    error={Boolean(error)}
                    helperText={error || attribute.hint || undefined}
                    InputLabelProps={{shrink: true}}
                />
                {/* Same styles as the TDAForm date-preset chips and the pressure-line title proposals */}
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{pt: '5px'}}>
                    {proposals.map((proposal) => (
                        <Chip
                            key={proposal.value}
                            label={proposal.name}
                            variant={value === proposal.value ? 'filled' : 'outlined'}
                            color={value === proposal.value ? 'primary' : 'default'}
                            size="small"
                            disabled={!!attribute.disabled}
                            onClick={() => setFieldValue(attribute.index, proposal.value)}
                            data-testid="role-proposal-chip"
                        />
                    ))}
                </Stack>
            </div>
        );
    },
    [LINKED_READ_ONLY_INPUT_TYPE]: ({attribute, value}: any) => (
        <TextField
            id={attribute.index}
            name={attribute.index}
            fullWidth
            size="small"
            disabled
            label={attribute.name}
            value={value ?? ''}
            InputLabelProps={{shrink: true}}
            inputProps={{'data-testid': `linked-readonly-${attribute.index}`}}
        />
    ),
});
